'use client';

import React from 'react';
import { Button } from '@tranquility-ai/theme-components/button';
import '@tranquility-ai/theme-components/button.css';

import { useTimePilotT } from '@/i18n/useTimePilotT';
import { cn } from '@/lib/utils';

export type MediaDisplayCaptureActionsProps = {
  isDragging: boolean;
  hasCapture: boolean;
  onCancel: () => void;
  onUseOriginal: () => void;
  onUseCapture: () => void;
  /** Brand chrome + dimmed controls while framing a capture. */
  isCaptureMode?: boolean;
  /** When set, Cancel keeps the thumbnail-edit "done" test id. */
  cancelTestId?: string;
  /** Shown in place of the status text when a still could not be made. */
  errorMessage?: string;
  /** True while the still for the current selection is being produced. */
  isBusy?: boolean;
  /** Extra reason to keep Use Thumbnail disabled (e.g. All Cases with no owning case). */
  commitDisabledReason?: string;
};

export function MediaDisplayCaptureActions({
  isDragging,
  hasCapture,
  onCancel,
  onUseOriginal,
  onUseCapture,
  isCaptureMode = false,
  cancelTestId,
  errorMessage,
  isBusy = false,
  commitDisabledReason,
}: MediaDisplayCaptureActionsProps) {
  const t = useTimePilotT();
  const commitDisabled = isDragging || !hasCapture || Boolean(commitDisabledReason);
  const dimmed = isCaptureMode ? 'opacity-[0.33]' : undefined;

  return (
    <div
      data-testid="capture-pane-actions"
      data-capture-mode={isCaptureMode ? 'true' : undefined}
      className={cn(
        'flex w-full shrink-0 items-center justify-between px-4 py-2',
        isCaptureMode
          ? 'bg-[var(--surface-brand,#0387fe)]'
          : 'bg-[var(--color-ink-075,#1a263e)]',
      )}
    >
      <Button
        type="button"
        size="md"
        style="text"
        color="light"
        background={isCaptureMode ? 'brand' : 'inverse'}
        disabled={isDragging}
        data-testid={cancelTestId}
        className={dimmed}
        onClick={onCancel}
      >
        {t('media.cancel')}
      </Button>
      {isBusy ? (
        <span
          data-testid="capture-pane-status"
          className="px-2 text-center text-xs text-[color:var(--text-inverse,#ffffff)]/80"
        >
          {t('media.creatingCapture')}
        </span>
      ) : errorMessage ? (
        <span
          role="status"
          data-testid="capture-pane-error"
          className="px-2 text-center text-xs text-[color:var(--text-inverse,#ffffff)]"
        >
          {errorMessage}
        </span>
      ) : null}
      <div className={cn('flex items-center gap-2', dimmed)}>
        <Button
          type="button"
          size="md"
          style="text"
          color="light"
          background={isCaptureMode ? 'brand' : 'inverse'}
          disabled={isDragging}
          data-testid="capture-use-original"
          onClick={onUseOriginal}
        >
          {t('media.useFullMedia')}
        </Button>
        <Button
          type="button"
          size="md"
          style="solid"
          color="accent"
          background={isCaptureMode ? 'brand' : 'inverse'}
          disabled={commitDisabled}
          data-testid="capture-use-capture"
          onClick={onUseCapture}
        >
          {t('media.useCapture')}
        </Button>
      </div>
    </div>
  );
}
